import axios, { type AxiosError } from 'axios'
import { appPath } from '@/utils/paths'
import { loadCredentials, clearCredentials } from '@/utils/credentialStorage'
import { getCookie, getSelectedRestaurantId } from './requestMetadata'

export const apiClient = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: send cookies with requests
})

// Add auth token and CSRF token interceptor
apiClient.interceptors.request.use((config) => {
  // Add auth token if present
  const authToken = localStorage.getItem('authToken')
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`
  }

  // Add CSRF token for mutation requests (Play Framework expects this)
  const csrfToken = getCookie('PLAY_CSRF_TOKEN')
  if (csrfToken && config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
    config.headers['Csrf-Token'] = csrfToken
  }

  // Pin every admin request to the currently selected restaurant context for backend validation.
  const selectedRestaurantId = getSelectedRestaurantId()
  if (selectedRestaurantId) {
    config.headers['X-Selected-Restaurant-Id'] = String(selectedRestaurantId)
  }

  return config
})

// Auto-re-auth on 401 is available only for persisted restaurant credentials.
let isReAuthenticating: Promise<boolean> | null = null

const NO_SILENT_REAUTH_ENDPOINTS = [
  '/handleLogin',
  '/api/admin/restaurant-login',
  '/api/admin/auth/status',
  '/api/admin/auth/config',
  '/api/admin/account/security/',
]

/** Fired after a successful silent re-login so long-lived consumers
 *  (e.g. the SSE order stream, which only authenticates via cookie)
 *  can reconnect immediately instead of waiting for their own backoff. */
export const SESSION_RESTORED_EVENT = 'auth:session-restored'

interface ReAuthFlags {
  _skipReAuth?: boolean
  _retriedAfterReAuth?: boolean
}

function attemptReAuth(): Promise<boolean> {
  const creds = loadCredentials()
  if (!creds) return Promise.resolve(false)

  const request = apiClient.post(
    '/api/admin/restaurant-login',
    { restaurantId: creds.restaurantId, accessKey: creds.accessKey },
    { _skipReAuth: true } as never,
  )

  return request
    .then((res) => {
      const valid = (res.data as { valid?: boolean }).valid === true
      if (!valid) {
        // Definitive rejection — the stored credentials are wrong
        clearCredentials()
      } else {
        window.dispatchEvent(new CustomEvent(SESSION_RESTORED_EVENT))
      }
      return valid
    })
    // Network error / 5xx is transient — keep credentials for the next attempt
    .catch(() => false)
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (typeof error.config & ReAuthFlags) | undefined
    if (
      error.response?.status !== 401 ||
      !config ||
      config._skipReAuth ||
      NO_SILENT_REAUTH_ENDPOINTS.some((ep) => config.url?.includes(ep))
    ) {
      return Promise.reject(error)
    }

    // A retried request that 401s again means the session is fine but this
    // endpoint rejects us (e.g. scope mismatch). Surface the error instead of
    // looping login -> retry -> 401 forever.
    if (config._retriedAfterReAuth) {
      return Promise.reject(error)
    }

    // Deduplicate: if a re-auth is already in flight, wait for it
    isReAuthenticating ??= attemptReAuth().finally(() => {
      isReAuthenticating = null
    })

    const success = await isReAuthenticating
    if (success) {
      // Retry the original request exactly once
      config._retriedAfterReAuth = true
      return apiClient.request(config)
    }

    // Re-auth failed. Only force the login screen when the credentials were
    // definitively rejected (attemptReAuth cleared them); on transient
    // failures the request errors out and the UI shows its error state.
    if (!loadCredentials()) {
      window.location.href = appPath('login')
    }
    return Promise.reject(error)
  }
)
